import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

/**
 * Returns the signed-in user's referral code, share link, stats, and (for venues)
 * how many platform-fee waivers they have remaining.
 */
export const getMyReferralStatus = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data: prof } = await supabase
      .from("profiles")
      .select("referral_code, referred_by_user_id, venue_fee_waivers_remaining, role")
      .eq("id", userId)
      .maybeSingle();

    const code = (prof as { referral_code?: string | null } | null)?.referral_code ?? null;
    const role = (prof as { role?: string | null } | null)?.role ?? null;
    const waivers = (prof as { venue_fee_waivers_remaining?: number } | null)?.venue_fee_waivers_remaining ?? 0;

    // Referrals I've sent, with referred user info for the history list
    const { data: sent } = await supabase
      .from("referrals")
      .select("id, referred_id, status, kind, created_at, rewarded_at, reward_booking_id")
      .eq("referrer_id", userId)
      .order("created_at", { ascending: false });
    const rows = sent ?? [];
    const pending = rows.filter((r) => r.status === "pending").length;
    const rewarded = rows.filter((r) => r.status === "rewarded").length;

    // Referral link click tracking (from shared recap cards, profile links, etc.)
    const { data: clickRows } = await supabase
      .from("referral_link_clicks" as never)
      .select("created_at, source, signed_up")
      .eq("referrer_user_id", userId)
      .order("created_at", { ascending: false })
      .limit(500);
    const clicks = (clickRows ?? []) as Array<{ created_at: string; source: string | null; signed_up: boolean }>;
    const clicksBySource: Record<string, number> = {};
    for (const c of clicks) {
      const k = c.source ?? "unknown";
      clicksBySource[k] = (clicksBySource[k] ?? 0) + 1;
    }
    const clicks_total = clicks.length;
    const clicks_signed_up = clicks.filter((c) => c.signed_up).length;

    const referredIds = Array.from(
      new Set(rows.map((r) => r.referred_id).filter(Boolean)),
    ) as string[];
    const nameById = new Map<string, string>();
    if (referredIds.length > 0) {
      const { data: people } = await supabase
        .from("profiles")
        .select("id, full_name")
        .in("id", referredIds);
      for (const p of people ?? []) {
        nameById.set(p.id as string, ((p.full_name as string) ?? "").trim());
      }
    }

    const history = rows.map((r) => {
      const raw = nameById.get(r.referred_id as string) ?? "";
      // Privacy: show first name + last initial only
      let display = "New member";
      if (raw) {
        const parts = raw.split(/\s+/).filter(Boolean);
        display = parts[0] ?? "Member";
        if (parts[1]) display += ` ${parts[1][0]}.`;
      }
      return {
        id: r.id as string,
        kind: r.kind as string,
        status: r.status as string,
        created_at: r.created_at as string,
        rewarded_at: (r.rewarded_at as string | null) ?? null,
        reward_granted: Boolean(r.reward_booking_id) || r.status === "rewarded",
        referred_name: display,
      };
    });

    return {
      code,
      role,
      venue_fee_waivers_remaining: waivers,
      pending_count: pending,
      rewarded_count: rewarded,
      clicks_total,
      clicks_signed_up,
      clicks_by_source: clicksBySource,
      referred_by_set: Boolean((prof as { referred_by_user_id?: string | null } | null)?.referred_by_user_id),
      history,
    };
  });

/**
 * Called immediately after signup to attach a referrer to the new user.
 * No-op if the code is invalid, missing, self-referring, or the user already has a referrer.
 */
export const applyReferralCode = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ code: z.string().trim().min(3).max(32) }).parse(d))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: ok, error } = await supabase.rpc("apply_referral_code", {
      _user_id: userId,
      _code: data.code,
    });
    if (error) return { ok: false as const, reason: error.message };

    // Attribute this signup to the most recent unattributed click on this code.
    if (ok) {
      try {
        const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
        const { data: candidate } = await supabaseAdmin
          .from("referral_link_clicks" as never)
          .select("id")
          .eq("referral_code", data.code)
          .eq("signed_up", false)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();
        const clickId = (candidate as { id?: string } | null)?.id;
        if (clickId) {
          await supabaseAdmin
            .from("referral_link_clicks" as never)
            .update({ signed_up: true, signed_up_user_id: userId } as never)
            .eq("id", clickId);
        }
      } catch { /* non-fatal */ }
    }

    return { ok: Boolean(ok) };
  });
